import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { renderEmphasis } from "@/lib/richText";

export default function Books() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});

  useSeo({
    title: "Books",
    description: "Novels, essays and poetry by Gemma Veda — a small library of contemplative work.",
  });

  useEffect(() => {
    api.get("/books")
      .then(({ data }) => setBooks(data))
      .finally(() => setLoading(false));
    api.get("/settings").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <section data-testid="books-page" className="pt-36 pb-20 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="gv-label">The Library</div>
            <h1 className="font-serif text-5xl md:text-7xl mt-4 text-[#1A1F24] leading-[1.05] max-w-3xl" data-testid="books-heading">
              {renderEmphasis(content.books_heading || "The *Soul's CODE* series.")}
            </h1>
            <p className="mt-6 text-[#5C6670] text-lg max-w-2xl font-light leading-relaxed" data-testid="books-subtext">
              {content.books_subtext || "A journey to soul searching, a tapestry of awakening — written by Gemma Veda. Available in Kindle and paperback through Amazon."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading && <div className="text-center text-[#5C6670] py-20">Loading…</div>}
          {!loading && books.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif text-2xl italic text-[#5C6670]">The shelves are still being arranged.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16">
            {books.map((b, i) => (
              <motion.div
                key={b.id}
                data-testid={`book-card-${b.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group"
              >
                <div className="aspect-[2/3] overflow-hidden bg-[#E5E5E5] shadow-md hover-lift">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />
                </div>
                <div className="pt-6">
                  {b.publish_year && (
                    <div className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">{b.publish_year}</div>
                  )}
                  <h3 className="font-serif text-3xl mt-3 text-[#1A1F24] leading-tight">{b.title}</h3>
                  {b.subtitle && <p className="mt-1 text-[#5C6670] italic font-light">{b.subtitle}</p>}
                  <p className="mt-4 text-[#1A1F24] text-base font-light leading-relaxed">{b.description}</p>
                  {b.buy_link && (
                    <a
                      href={b.buy_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-buy-${b.id}`}
                      className="inline-block mt-5 text-sm tracking-[0.2em] uppercase text-[#C5A059] gv-link"
                    >
                      Read on Amazon →
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
